import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { loginParticipant, forgotPassword } from '@/lib/api';
import { RTBLogo } from '@/components/RTBLogo';

export default function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });
  const [forgotMode, setForgotMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token } = await loginParticipant(form);
      localStorage.setItem('rtb_user_token', token);
      toast.success('Welcome back!');
      navigate('/portal');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) { toast.error('Enter your email'); return; }
    setLoading(true);
    try {
      await forgotPassword(resetEmail);
      setResetSent(true);
      toast.success('Check your email for a reset link');
    } catch { toast.error('Failed to send reset link'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-rtb-mint/30 flex items-center justify-center px-4" data-testid="login-page">
      {/* Decorative blobs */}
      <div className="fixed top-20 right-20 w-72 h-72 bg-rtb-purple/5 rounded-full blur-[100px]" />
      <div className="fixed bottom-20 left-20 w-72 h-72 bg-rtb-green/5 rounded-full blur-[100px]" />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-md w-full relative z-10">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <RTBLogo className="h-10 mx-auto" />
          </Link>
          <h1 className="text-3xl font-oswald font-bold uppercase text-gray-900">
            Welcome <span className="text-rtb-purple">Back</span>
          </h1>
          <p className="text-gray-500 text-sm mt-2">Sign in to your participant portal</p>
        </div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="glass-light p-8 space-y-5 rounded-2xl">
          {!forgotMode ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="text-gray-600 mb-1.5 block text-sm font-medium">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="input-styled" required data-testid="login-email" />
              </div>
              <div className="relative">
                <Label className="text-gray-600 mb-1.5 block text-sm font-medium">Password</Label>
                <Input type={showPassword ? "text" : "password"} value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="input-styled pr-12" required data-testid="login-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-[38px] text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="text-right">
                <button type="button" onClick={() => { setForgotMode(true); setResetEmail(form.email); }}
                  className="text-rtb-purple text-sm hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </button>
              </div>
              <Button type="submit" disabled={loading} className="btn-primary w-full" data-testid="login-submit-btn">
                {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> :
                  <>Sign In <ArrowRight className="ml-2 w-5 h-5" /></>}
              </Button>
              <p className="text-center text-gray-400 text-sm">
                Don't have an account? <Link to="/register" className="text-rtb-purple font-semibold hover:underline">Register</Link>
              </p>
            </form>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              {resetSent ? (
                <div className="text-center py-4">
                  <CheckCircle className="w-12 h-12 text-rtb-green mx-auto mb-4" />
                  <h3 className="font-oswald font-bold text-lg text-gray-900 mb-2">Check Your Email</h3>
                  <p className="text-gray-500 text-sm">If an account exists for <strong>{resetEmail}</strong>, we've sent a password reset link.</p>
                </div>
              ) : (
                <>
                  <div>
                    <Label className="text-gray-600 mb-1.5 block text-sm font-medium">Email Address</Label>
                    <Input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
                      className="input-styled" required placeholder="Enter your account email" data-testid="forgot-email" />
                  </div>
                  <Button type="submit" disabled={loading} className="btn-primary w-full" data-testid="send-reset-btn">
                    {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> :
                      'Send Reset Link'}
                  </Button>
                </>
              )}
              <p className="text-center">
                <button type="button" onClick={() => { setForgotMode(false); setResetSent(false); }}
                  className="text-rtb-purple text-sm hover:underline" data-testid="back-to-login">
                  Back to Sign In
                </button>
              </p>
            </form>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
}
